// packages
import { Fragment, useMemo, useState } from 'react'
import { Info, Plus, Trash2 } from 'lucide-react'

// components
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { MarkerInsightPanel } from './marker-insight-panel'

// libs
import { cn } from '@/lib/utils'

// entities
import {
  ExamCollection,
  ExamMarkerValues,
  marker_status,
  exam_analysis_category
} from '@/entities/exam-analysis'
import { MarkerDef, MarkerRangeKey } from './markers/types'
import {
  STATUS_INPUT_CLASS,
  STATUS_BADGE_CLASS,
  STATUS_LABEL,
  classifyMarker,
  getMarkerRange,
  getHomemSplitInfo,
  groupMarkers,
  partitionMarkerIds
} from './marker-utils'

interface MarkersTableProps {
  category: exam_analysis_category
  markers: MarkerDef[]
  rangeKey: MarkerRangeKey
  collections: ExamCollection[]
  values: ExamMarkerValues
  onValuesChange: (next: ExamMarkerValues) => void
  onCollectionsChange: (next: ExamCollection[]) => void
  onAddCollection: () => void
  onRemoveCollection: (ref: string) => void
}

type RenderRow =
  | { kind: 'section-divider'; label: string }
  | { kind: 'group-separator'; label: string }
  | { kind: 'marker'; marker: MarkerDef }

export function MarkersTable({
  category,
  markers,
  rangeKey,
  collections,
  values,
  onValuesChange,
  onCollectionsChange,
  onAddCollection,
  onRemoveCollection
}: MarkersTableProps) {
  const [openInsights, setOpenInsights] = useState<Set<string>>(new Set())

  const rows: RenderRow[] = useMemo(() => {
    const homemSplit = getHomemSplitInfo(category)
    const out: RenderRow[] = []
    if (homemSplit) {
      out.push({ kind: 'section-divider', label: 'Espermograma' })
      groupMarkers(homemSplit.espermograma).forEach(g => {
        out.push({ kind: 'group-separator', label: g.group })
        g.items.forEach(m => out.push({ kind: 'marker', marker: m }))
      })
      out.push({ kind: 'section-divider', label: 'Homens' })
      groupMarkers(homemSplit.homem).forEach(g => {
        out.push({ kind: 'group-separator', label: g.group })
        g.items.forEach(m => out.push({ kind: 'marker', marker: m }))
      })
    } else {
      groupMarkers(markers).forEach(g => {
        out.push({ kind: 'group-separator', label: g.group })
        g.items.forEach(m => out.push({ kind: 'marker', marker: m }))
      })
    }
    return out
  }, [category, markers])

  const setMarkerValue = (markerId: string, collectionRef: string, value: string) => {
    const next: ExamMarkerValues = { ...values }
    const byCol = { ...(next[markerId] || {}) }
    if (value.trim() === '') delete byCol[collectionRef]
    else byCol[collectionRef] = value
    next[markerId] = byCol
    onValuesChange(next)
  }

  const setCollectionDate = (collectionRef: string, dateIso?: string | null) => {
    const next = collections.map(c => (c.ref === collectionRef ? { ...c, date: dateIso ?? null } : c))
    onCollectionsChange(next)
  }

  const toggleInsight = (markerId: string) => {
    setOpenInsights(prev => {
      const next = new Set(prev)
      if (next.has(markerId)) next.delete(markerId)
      else next.add(markerId)
      return next
    })
  }

  // # colunas total: 1 (marcador) + N (coletas) + 1 (nova) + 1 (info) = N + 3
  const totalCols = collections.length + 3

  if (markers.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        Nenhum marcador disponível para a categoria selecionada.
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-card shadow-soft overflow-x-auto">
      <Table className="text-sm min-w-full">
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead className="w-[26%] min-w-[200px] align-bottom">Marcador</TableHead>
            {collections.map((c, idx) => {
              const isLast = idx === collections.length - 1
              const hasAnyValueInThisCol = Object.values(values).some(byCol => {
                const v = byCol[c.ref]
                return v !== undefined && v !== null && String(v).trim() !== ''
              })
              const missingDate = !c.date && hasAnyValueInThisCol
              return (
                <TableHead key={c.ref} className={cn('min-w-[170px] align-bottom', isLast && 'bg-primary/5')}>
                  <div className="flex flex-col gap-1 py-1 normal-case font-normal">
                    <div className="flex items-center justify-between gap-1">
                      <span className={cn('text-[10px] font-bold uppercase tracking-wider', isLast ? 'text-coentro' : 'text-muted-foreground')}>
                        Coleta {idx + 1}
                        {isLast ? ' · atual' : ''}
                      </span>
                      {collections.length > 1 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => onRemoveCollection(c.ref)}
                              className="h-6 w-6 p-0 shrink-0"
                              aria-label={`Remover coleta ${idx + 1}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">Remover coleta</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <DatePicker
                      size="sm"
                      value={c.date || undefined}
                      onChange={v => setCollectionDate(c.ref, v ?? null)}
                      placeholder="dd/mm/aaaa"
                      aria-label={`Data da coleta ${idx + 1}`}
                      className={missingDate ? '[&_>div]:border-red-500 [&_>div]:ring-1 [&_>div]:ring-red-300' : ''}
                    />
                    {missingDate && (
                      <span className="text-[10px] text-red-600 font-medium">Data obrigatória</span>
                    )}
                  </div>
                </TableHead>
              )
            })}
            <TableHead className="min-w-[140px] align-bottom">
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={onAddCollection}
                className="gap-1 text-xs"
              >
                <Plus className="h-3.5 w-3.5" />
                Nova coleta
              </Button>
            </TableHead>
            <TableHead className="w-[50px] align-bottom text-center">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Info</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, idx) => {
            if (row.kind === 'section-divider') {
              return (
                <TableRow key={`section-${row.label}-${idx}`} className="hover:bg-transparent">
                  <TableCell colSpan={totalCols} className="p-0">
                    <div className="px-4 py-2 bg-coentro text-white text-xs font-bold uppercase tracking-wider">{row.label}</div>
                  </TableCell>
                </TableRow>
              )
            }
            if (row.kind === 'group-separator') {
              return (
                <TableRow key={`sep-${row.label}-${idx}`} className="hover:bg-transparent">
                  <TableCell colSpan={totalCols} className="p-0">
                    <div
                      className="h-7 bg-muted text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center px-4 border-y border-border"
                      aria-label={`Grupo: ${row.label}`}
                    >
                      {row.label}
                    </div>
                  </TableCell>
                </TableRow>
              )
            }

            const m = row.marker
            const range = getMarkerRange(m, rangeKey)
            const byCol = values[m.id] || {}
            const isOpen = openInsights.has(m.id)

            return (
              <Fragment key={m.id}>
                <TableRow className="align-top">
                  <TableCell className="py-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-start gap-1.5">
                        <span className="font-medium text-foreground">{m.name}</span>
                        {m.obs && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="text-muted-foreground hover:text-foreground mt-0.5" aria-label={`Observação: ${m.name}`}>
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs">
                              {m.obs}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground leading-snug whitespace-pre-wrap">
                        {range?.display ?? '—'}
                      </span>
                    </div>
                  </TableCell>

                  {collections.map((c, ci) => {
                    const isLast = ci === collections.length - 1
                    const v = byCol[c.ref] ?? ''
                    const status = classifyMarker(v, range)
                    return (
                      <TableCell key={c.ref} className={cn('py-3', isLast && 'bg-primary/5')}>
                        <div className="flex flex-col gap-1">
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="—"
                            value={v}
                            onChange={e => setMarkerValue(m.id, c.ref, e.target.value)}
                            className={cn('h-9', STATUS_INPUT_CLASS[status])}
                          />
                          <div className="flex items-center gap-1.5 min-h-[16px]">
                            {status !== marker_status.EMPTY && (
                              <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider', STATUS_BADGE_CLASS[status])}>
                                {STATUS_LABEL[status]}
                              </span>
                            )}
                            {range?.unit && status !== marker_status.EMPTY && (
                              <span className="text-[10px] text-muted-foreground">{range.unit}</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                    )
                  })}

                  <TableCell className="py-3" />

                  <TableCell className="py-3 text-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant={isOpen ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => toggleInsight(m.id)}
                          className={cn('h-7 w-7 p-0 rounded-md text-xs font-bold', isOpen ? 'bg-coentro text-white hover:bg-coentro/90' : 'text-coentro border-coentro/30 hover:bg-coentro/10')}
                          aria-label={`${isOpen ? 'Fechar' : 'Abrir'} insights de ${m.name}`}
                        >
                          i
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">{isOpen ? 'Fechar insights' : 'Ver insights e evolução'}</TooltipContent>
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {isOpen && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={totalCols} className="p-0">
                      <MarkerInsightPanel marker={m} range={range} collections={collections} valuesByCollection={byCol} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}

          {/* Marcadores legados (não estão mais na lista atual da metodologia) */}
          {(() => {
            const { orphans } = partitionMarkerIds(values, markers)
            if (orphans.length === 0) return null
            return (
              <Fragment key="__orphans__">
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={totalCols} className="p-0">
                    <div className="px-4 py-2 bg-amber-100 text-amber-900 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                      Marcadores legados
                      <span className="text-[10px] font-normal normal-case text-amber-800/80">
                        (não estão mais na lista atual da metodologia — mantidos para preservar histórico)
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
                {orphans.map(id => {
                  const byCol = values[id] || {}
                  return (
                    <TableRow key={`orphan-${id}`} className="align-top opacity-80">
                      <TableCell className="py-3">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-foreground italic">Marcador legado</span>
                          <span className="text-[10px] text-muted-foreground font-mono">ID: {id}</span>
                        </div>
                      </TableCell>
                      {collections.map(c => {
                        const v = byCol[c.ref] ?? ''
                        return (
                          <TableCell key={c.ref} className="py-3">
                            <Input type="text" value={v} disabled className="h-9 bg-muted/50 italic text-muted-foreground" />
                          </TableCell>
                        )
                      })}
                      <TableCell className="py-3" />
                      <TableCell className="py-3" />
                    </TableRow>
                  )
                })}
              </Fragment>
            )
          })()}
        </TableBody>
      </Table>
    </div>
  )
}
